import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferrerSchema } from '../ReferrerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import { ReferralStoredDocumentsSchema } from '../ReferralDocumentsSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { payments } from '../../../data';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

// Metadata from SFTP PDF parsing — preserved for debugging/audit
const ClukMetadataSchema = z
  .object({
    source_fields: z.unknown().optional(),
    client: z.string().optional(),
    form_type: z.string().optional(),
  })
  .optional();

const paymentSchema = z
  .string()
  .refine((payment) => payments.includes(payment));

const clukBaseFields = {
  patient: ReferralPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  payment: paymentSchema,
  referrer: ReferrerSchema.extend({
    id: z.string().optional(),
  }),
  referral: z
    .object({
      serviceId: z.string().optional(),
      hasPreferredProfessional: z.boolean().optional(),
      preferredProfessionalId: z.string().default(''),
    })
    .optional(),
  joinReasonId: joinReasonIdSchema(allJoinReasonIds).optional(),
  // Documents attached to the referral, sent as the canonical `documents`
  // array of the body (PPL-3549)
  documents: ReferralStoredDocumentsSchema,
  _metadata: ClukMetadataSchema,
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

// Schema for validating incoming POST /referrals request.
// No consent evidence is required of the body: Canada Life has no referral
// website, so nobody is there to answer the question — the integration
// referring a member IS the attestation, and postClukReferral records it as
// such (referrerAttestedConsent). A body that does state its evidence is
// honoured.
export const ReferralCreateClukSchema = withLegacyPlanIdentifiers(
  z.object(clukBaseFields).check(requiresPatientPhoneForConsent),
);

// Schema for how it's stored in Firestore (extends base with status, type, etc.)
export const ReferralClukSchema = ReferralBaseSchema.extend({
  ...clukBaseFields,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
});

export type ReferralCreateCluk = z.infer<typeof ReferralCreateClukSchema>;
export type ReferralClukData = z.infer<typeof ReferralClukSchema>;
export type ReferralCluk = IObject.HasIdTimestamped<ReferralClukData>;
